import { useCallback, useRef, useState } from "react";

// Undo/redo history; quick consecutive edits (e.g. typing) are grouped into one step.
export function useUndoable(limit = 30, groupMs = 800) {
  const [h, setH] = useState({ past: [], present: null, future: [], steps: 0 });
  const last = useRef(0);

  const set = useCallback((fn) => {
    const now = Date.now();
    const group = now - last.current < groupMs;
    last.current = now;
    setH((s) => {
      const next = typeof fn === "function" ? fn(s.present) : fn;
      if (next === s.present) return s;
      const grouped = group && s.past.length > 0;
      return { past: grouped ? s.past : [...s.past, s.present].slice(-limit), present: next, future: [], steps: grouped ? s.steps : s.steps + 1 };
    });
  }, [limit, groupMs]);

  const patch = useCallback((fn) => setH((s) => ({ ...s, present: typeof fn === "function" ? fn(s.present) : fn })), []);
  const reset = useCallback((v) => setH({ past: [], present: v, future: [], steps: 0 }), []);

  // Restored snapshots keep the saved identity so autosave never creates a duplicate quote.
  const keepIds = (v, cur) => (cur?.id ? { ...v, id: cur.id, number: cur.number, code: cur.code, year: cur.year } : v);

  const undo = useCallback(() => {
    last.current = 0;
    setH((s) => (s.past.length ? { past: s.past.slice(0, -1), present: keepIds(s.past[s.past.length - 1], s.present), future: [s.present, ...s.future].slice(0, limit), steps: s.steps + 1 } : s));
  }, [limit]);
  const redo = useCallback(() => {
    last.current = 0;
    setH((s) => (s.future.length ? { past: [...s.past, s.present].slice(-limit), present: keepIds(s.future[0], s.present), future: s.future.slice(1), steps: s.steps + 1 } : s));
  }, [limit]);

  return { value: h.present, set, patch, reset, undo, redo, canUndo: h.past.length, canRedo: h.future.length, steps: h.steps };
}
